import { Helmet } from "react-helmet-async";
import { ArrowLeft, MapPin, Phone, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

const WHATSAPP_URL = "https://wa.me/919891315777?text=Hi%2C%20I%20want%20to%20book%20a%20water%20tank%20cleaning%20service%20in%20Gurugram";

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>About TankBuddy - Gurugram's Trusted Water Tank Cleaning Service</title>
        <meta name="description" content="Learn about TankBuddy, Gurugram's most trusted premium water tank cleaning service. Certified technicians, eco-friendly products, serving since 2020. Call +91 98913 15777" />
        <link rel="canonical" href="https://www.tankbuddy.in/about" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Header />

      <main className="pt-20 pb-16">
        <section className="max-w-4xl mx-auto px-4 md:px-8 py-12">

          <a href="/" className="inline-flex items-center gap-2 text-sky text-sm font-medium mb-8 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </a>

          <div className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full mb-6">
            <MapPin className="w-4 h-4" /> Gurugram, Haryana, India
          </div>

          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mb-4">
            About TankBuddy
          </h1>
          <p className="text-slate-500 text-lg mb-10">
            Gurugram's most trusted premium water tank cleaning service. Protecting families since 2020.
          </p>

          <div className="bg-navy rounded-xl p-8 mb-12 text-white">
            <h2 className="font-heading font-bold text-xl mb-2">Book a Cleaning Today</h2>
            <p className="text-slate-300 text-sm mb-5">Same-day service available. Starting Rs. 999 for residential tanks.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-whatsapp hover:bg-whatsapp-hover text-white rounded-full px-6">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-4 h-4 mr-2" /> Book on WhatsApp
                </a>
              </Button>
              <Button asChild variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-6">
                <a href="tel:+919891315777">
                  <Phone className="w-4 h-4 mr-2" /> Call +91 98913 15777
                </a>
              </Button>
            </div>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">Our Story</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              TankBuddy was founded with one mission: to make clean, safe drinking water accessible to every family in Gurugram. We saw that most homeowners had no idea when their water tank was last cleaned, and that this was directly causing waterborne illnesses across the city.
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              Starting in 2020 with a small team of certified technicians and a commitment to eco-friendly practices, we have grown to become Gurugram's most trusted water tank cleaning service. Today we serve over 2,000 families, housing societies, hotels and commercial properties across all Gurugram sectors.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Every cleaning we do is backed by our 30-day hygiene guarantee. If your water is not 100% bacteria-free after our service, we come back at no charge.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-10">
            <div className="bg-surface border border-slate-200 rounded-xl p-6">
              <span className="text-sky font-bold text-3xl font-heading">2000+</span>
              <p className="text-navy font-semibold mt-1">Happy Customers</p>
              <p className="text-slate-500 text-sm mt-1">Families and businesses served across Gurugram</p>
            </div>
            <div className="bg-surface border border-slate-200 rounded-xl p-6">
              <span className="text-sky font-bold text-3xl font-heading">4.9</span>
              <p className="text-navy font-semibold mt-1">Average Rating</p>
              <p className="text-slate-500 text-sm mt-1">Based on customer reviews across all platforms</p>
            </div>
            <div className="bg-surface border border-slate-200 rounded-xl p-6">
              <span className="text-sky font-bold text-3xl font-heading">5+</span>
              <p className="text-navy font-semibold mt-1">Years of Experience</p>
              <p className="text-slate-500 text-sm mt-1">Serving Gurugram since 2020</p>
            </div>
            <div className="bg-surface border border-slate-200 rounded-xl p-6">
              <span className="text-sky font-bold text-3xl font-heading">50+</span>
              <p className="text-navy font-semibold mt-1">Housing Societies</p>
              <p className="text-slate-500 text-sm mt-1">RWAs and societies trust TankBuddy</p>
            </div>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">Why Choose TankBuddy</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-surface border border-slate-200 rounded-lg p-5">
                <span className="text-sky font-bold text-sm">01</span>
                <h3 className="font-heading font-bold text-navy mt-1 mb-1">Certified Technicians</h3>
                <p className="text-slate-600 text-sm">All our technicians are trained, certified and background-verified for your peace of mind.</p>
              </div>
              <div className="bg-surface border border-slate-200 rounded-lg p-5">
                <span className="text-sky font-bold text-sm">02</span>
                <h3 className="font-heading font-bold text-navy mt-1 mb-1">Eco-Friendly Products</h3>
                <p className="text-slate-600 text-sm">We use food-grade, eco-friendly anti-bacterial products that are safe for your family and the environment.</p>
              </div>
              <div className="bg-surface border border-slate-200 rounded-lg p-5">
                <span className="text-sky font-bold text-sm">03</span>
                <h3 className="font-heading font-bold text-navy mt-1 mb-1">Same-Day Service</h3>
                <p className="text-slate-600 text-sm">We offer same-day tank cleaning across all Gurugram sectors, 7 days a week.</p>
              </div>
              <div className="bg-surface border border-slate-200 rounded-lg p-5">
                <span className="text-sky font-bold text-sm">04</span>
                <h3 className="font-heading font-bold text-navy mt-1 mb-1">30-Day Guarantee</h3>
                <p className="text-slate-600 text-sm">Every cleaning is backed by our 30-day hygiene guarantee. We return at no cost if needed.</p>
              </div>
            </div>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">Our Services</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <a href="/residential-tank-cleaning-gurugram" className="flex items-center gap-3 bg-surface border border-slate-200 rounded-lg p-4 hover:border-sky/50 hover:shadow-sm transition-all">
                <div className="w-8 h-8 rounded-lg bg-sky/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-sky" />
                </div>
                <span className="text-sm font-medium text-navy">Residential Tank Cleaning</span>
              </a>
              <a href="/overhead-water-tank-cleaning-gurugram" className="flex items-center gap-3 bg-surface border border-slate-200 rounded-lg p-4 hover:border-sky/50 hover:shadow-sm transition-all">
                <div className="w-8 h-8 rounded-lg bg-sky/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-sky" />
                </div>
                <span className="text-sm font-medium text-navy">Overhead Tank Cleaning</span>
              </a>
              <a href="/underground-water-tank-cleaning-gurugram" className="flex items-center gap-3 bg-surface border border-slate-200 rounded-lg p-4 hover:border-sky/50 hover:shadow-sm transition-all">
                <div className="w-8 h-8 rounded-lg bg-sky/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-sky" />
                </div>
                <span className="text-sm font-medium text-navy">Underground Tank Cleaning</span>
              </a>
              <a href="/commercial-water-tank-cleaning-gurugram" className="flex items-center gap-3 bg-surface border border-slate-200 rounded-lg p-4 hover:border-sky/50 hover:shadow-sm transition-all">
                <div className="w-8 h-8 rounded-lg bg-sky/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-sky" />
                </div>
                <span className="text-sm font-medium text-navy">Commercial Tank Cleaning</span>
              </a>
            </div>
          </div>

          <div>
            <h2 className="font-heading font-bold text-xl text-navy mb-4">Areas We Serve</h2>
            <div className="flex flex-wrap gap-2">
              <a href="/water-tank-cleaning-dlf-phase-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />DLF Phase 1-5</a>
              <a href="/water-tank-cleaning-sushant-lok-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />Sushant Lok</a>
              <a href="/water-tank-cleaning-palam-vihar-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />Palam Vihar</a>
              <a href="/water-tank-cleaning-golf-course-road-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />Golf Course Road</a>
              <a href="/water-tank-cleaning-sohna-road-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />Sohna Road</a>
              <a href="/water-tank-cleaning-mg-road-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />MG Road</a>
              <a href="/water-tank-cleaning-nirvana-country-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />Nirvana Country</a>
              <a href="/water-tank-cleaning-udyog-vihar-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />Udyog Vihar</a>
            </div>
          </div>

        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
